import { type RefObject, useLayoutEffect, useState } from "react";

/**
 * True when an element's content is taller than its box, i.e. a line-clamp (or
 * any overflow) is truncating it. The `+ 1` absorbs sub-pixel rounding so a box
 * that fits exactly is not reported as clamped.
 */
export function isElementClamped(el: Pick<HTMLElement, "scrollHeight" | "clientHeight">): boolean {
  return el.scrollHeight > el.clientHeight + 1;
}

/**
 * Reports whether the referenced element's content overflows its (clamped) box,
 * i.e. whether a CSS line-clamp is actually truncating the text right now.
 *
 * Prefer this over a character-count heuristic: whether text overflows depends on
 * the rendered width, not just its length, so the same string can fit on a wide
 * card yet overflow on a narrow one.
 *
 * @param ref      Element to measure (the one carrying the line-clamp).
 * @param active   Only measure while `true`; when `false` the last value is retained.
 *                 Pass `!expanded` so removing the clamp on expand does not flip the
 *                 result to `false`.
 * @param resetKey Change to force a re-measure (e.g. the text content), for cases a
 *                 ResizeObserver misses because the clamped box keeps the same size.
 */
export function useIsClamped(
  ref: RefObject<HTMLElement | null>,
  active = true,
  resetKey?: unknown,
): boolean {
  const [clamped, setClamped] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is a re-measure trigger, not read in the body (a ResizeObserver misses text changes that keep the clamped box the same height)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    let raf = 0;
    const measure = () => setClamped(isElementClamped(el));
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    measure();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [ref, active, resetKey]);

  return clamped;
}
